/**
 * 消息操作菜单动作集（任务 11a；纯函数，可单测）。浮层组件与接线在 11b。
 *
 * 口径（legacy §6.1 + 后端实锤）：
 * - 完整动作集 = 回复/复制/编辑/另存为/置顶/转发/选择/删除；
 *   失败消息 = 重试/删除（仅这两项）；pending 在途消息不出菜单（返回空，
 *   与多选「排除失败/未确认」同口径——在途删除会与回显竞态，10a 已定）。
 * - 复制：仅文本。编辑：仅自己的文本（无时限，modify 接口，14.7）。
 *   另存为：图片/文件/视频（12c + 14.3）。
 * - 删除（func_chat.go BatchDeleteMsg 实锤）：私聊双方都可删且**物理删双方**
 *   （无 DelMySelf，确认文案须说清）；群聊需管理员/群主（gInfo.Type 1|2），
 *   普通成员连自己的消息都不能删。myRole 当前无本地来源（群角色归 14.25
 *   群信息 store），接线层先恒传 0 → 群聊不出删除项，落地后自然放开。
 * - ready=false 的动作是未实现占位：**11b 渲染时过滤掉**（不渲染灰项），
 *   后续任务（转发 14.8/多选 14.9/置顶 14.10）实现后把对应 READY 常量
 *   翻 true 即可，可用性规则全在本文件单点。
 */

import type { OutboxStatus } from '../worker/chat/outbox';
import type { MessageContent } from '../chat/messageModel';

export type MessageMenuActionId =
  | 'reply'
  | 'copy'
  | 'edit'
  | 'saveAs'
  | 'pin'
  | 'forward'
  | 'select'
  | 'delete'
  | 'retry'
  | 'discard';

export interface MessageMenuAction {
  readonly id: MessageMenuActionId;
  readonly label: string;
  /** 危险动作（删除类）：红色渲染 + 需确认的在动作实现里确认 */
  readonly danger: boolean;
  /** false = 规则上可用但尚未实现，11b 渲染时过滤 */
  readonly ready: boolean;
}

/** 已实现动作白名单。后续任务落地时在此翻 true（规则不动）。 */
const READY: Readonly<Record<MessageMenuActionId, boolean>> = {
  reply: true, // 14.6
  copy: true,
  edit: true, // 14.7
  saveAs: true, // 12c 图片 + 14.3 文件/视频
  pin: true, // 14.10（id>0 门禁在 MessageList 接线层；群 AllowTop 由服务端挡）
  forward: true, // 14.8（canForwardMessage 的 id>0 门禁在 MessageList 接线层）
  select: true, // 14.9（可选性细则在 selectionModel.selectableIdsOf，接线层过门禁）
  delete: true, // 11c 落地（管理员对他人消息四选项归 14.27）
  retry: true,
  discard: true,
};

const LABELS: Readonly<Record<MessageMenuActionId, string>> = {
  reply: '回复',
  copy: '复制',
  edit: '编辑',
  saveAs: '另存为',
  pin: '置顶',
  forward: '转发',
  select: '选择',
  delete: '删除',
  retry: '重试',
  discard: '删除',
};

export interface MessageMenuInput {
  /** undefined = 服务端行 */
  readonly sendState?: OutboxStatus;
  readonly outgoing: boolean;
  readonly contentKind: MessageContent['kind'];
  readonly isGroup: boolean;
  /** 我在本群的角色：0=成员 1=管理员 2=群主（私聊忽略；本地来源归 14.25） */
  readonly myRole: number;
  /** 已置顶（14.10）：pin 动作文案切「取消置顶」。缺省=false */
  readonly pinned?: boolean;
  /**
   * 阅后即焚（14.13c）：secret 消息不出「转发/选择」（即焚内容不可永久化；
   * selectionModel/forwardModel 同口径）。其余动作保留（旧版同级——回复/复制/
   * 置顶/删除照常）。缺省=false
   */
  readonly secret?: boolean;
  /**
   * 端到端加密（14.14b）：不出「编辑」（modify 明文落库后 is_encrypted 仍 1，
   * 收方解密必坏——14.14a Worker 同口径拒绝）、不出「转发/选择」（目标会话
   * 密钥不同，forwardModel 同口径）。复制/回复/置顶/删除照常（行已解密，
   * decryptFailed 占位行 contentKind='placeholder' 天然无复制）。缺省=false
   */
  readonly encrypted?: boolean;
}

function action(id: MessageMenuActionId, label?: string): MessageMenuAction {
  return {
    id,
    label: label ?? LABELS[id],
    danger: id === 'delete' || id === 'discard',
    ready: READY[id],
  };
}

/**
 * 计算一条消息的菜单动作（按展示顺序）。返回空数组 = 不弹菜单。
 * 调用方渲染时按需过滤 !ready（本层保留全量便于单测规则）。
 */
export function buildMessageMenu(input: MessageMenuInput): MessageMenuAction[] {
  // uploading（12b 图片上传中）与 pending 同口径：在途不出菜单，等定态
  if (input.sendState === 'pending' || input.sendState === 'uploading') return [];
  if (input.sendState === 'failed') return [action('retry'), action('discard')];

  const out: MessageMenuAction[] = [action('reply')];
  if (input.contentKind === 'text') out.push(action('copy'));
  if (input.outgoing && input.contentKind === 'text' && input.encrypted !== true) {
    out.push(action('edit'));
  }
  if (input.contentKind === 'image' || input.contentKind === 'file' || input.contentKind === 'video') {
    out.push(action('saveAs'));
  }
  out.push(action('pin', input.pinned === true ? '取消置顶' : undefined));
  if (input.secret !== true && input.encrypted !== true) {
    out.push(action('forward'), action('select'));
  }
  if (!input.isGroup || input.myRole === 1 || input.myRole === 2) out.push(action('delete'));
  return out;
}

/** 11b 渲染口径：只展示已实现动作。 */
export function readyMenuActions(input: MessageMenuInput): MessageMenuAction[] {
  return buildMessageMenu(input).filter((a) => a.ready);
}
